import React, { useCallback, useEffect, useState } from "react";
import { CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle } from "@mui/material";
import {
  athenaPreview, athenaSubmit, billingError, billingErrorBody, createFile, downloadBatch, listVisits, previewFile,
} from "./billingApi";
import { Button, Card, DummyPill, Field, Messages, inputCls, money, todayStr } from "./ui";

/*
 * Billing > Generate: pick ready claims, then
 *  - Create 837 file: "Check file" runs the server's claim + envelope + interchange validators without
 *    writing anything; "Create 837 file" writes the batch (server side, mode 600) and offers the download.
 *  - Submit via Athena: disabled unless the server's BILLING_ATHENA_SUBMIT flag is on; a preview (duplicate
 *    check against Athena's own claims) and a confirm step come first.
 */

const daysAgo = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const GenerateTab = ({ athenaEnabled, dummy, toast, onFileCreated }) => {
  const [from, setFrom] = useState(daysAgo(30));
  const [to, setTo] = useState(todayStr());
  const [rows, setRows] = useState([]);
  const [selected, setSelected] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [check, setCheck] = useState(null);
  const [created, setCreated] = useState(null);
  const [athena, setAthena] = useState(null);      // preview shown in the confirm dialog

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await listVisits({ from, to });
      const ready = (r.visits || []).filter((v) => v.claim_status === "ready");
      setRows(ready);
      setSelected((s) => s.filter((id) => ready.some((v) => v.appointmentid === id)));
    } catch (e) {
      toast(billingError(e, "Could not load claims"), "error");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [from, to]);

  useEffect(() => { load(); }, [load]);

  const toggle = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const allOn = rows.length > 0 && selected.length === rows.length;

  const runCheck = async () => {
    setBusy(true);
    setCreated(null);
    try {
      setCheck(await previewFile(selected));
    } catch (e) {
      toast(billingError(e, "Could not check the file"), "error");
    } finally {
      setBusy(false);
    }
  };

  const runCreate = async () => {
    setBusy(true);
    try {
      const out = await createFile(selected);
      setCreated(out);
      setCheck(null);
      setSelected([]);
      toast(`837 file created (${out.claims} claim${out.claims === 1 ? "" : "s"})`, "success");
      load();
      onFileCreated && onFileCreated();
    } catch (e) {
      const body = billingErrorBody(e);
      if (body?.detail) setCheck({ problems: body.detail.problems || [], envelope: body.detail.errors ? body.detail : null });
      toast(billingError(e, "Could not create the file"), "error");
    } finally {
      setBusy(false);
    }
  };

  const openAthena = async () => {
    setBusy(true);
    try {
      setAthena(await athenaPreview(selected));
    } catch (e) {
      toast(billingError(e, "Could not prepare the Athena submission"), "error");
    } finally {
      setBusy(false);
    }
  };

  const confirmAthena = async () => {
    setBusy(true);
    try {
      const out = await athenaSubmit(selected, athena.confirm_token);
      const ok = out.results.filter((r) => r.ok).length;
      toast(`${ok} of ${out.results.length} claim(s) sent to Athena`, ok === out.results.length ? "success" : "warning");
      setAthena(null);
      setSelected([]);
      load();
    } catch (e) {
      toast(billingError(e, "Athena submission failed"), "error");
    } finally {
      setBusy(false);
    }
  };

  const env = check?.envelope;
  const ic = check?.interchange;

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-end gap-3 bg-white rounded-xl border border-[#E5E7EB] px-4 py-3">
        <Field label="From"><input type="date" className={inputCls()} value={from} onChange={(e) => setFrom(e.target.value)} /></Field>
        <Field label="To"><input type="date" className={inputCls()} value={to} onChange={(e) => setTo(e.target.value)} /></Field>
        <Button kind="outline" onClick={load} disabled={loading}>Refresh</Button>
        <div className="ml-auto flex flex-wrap items-center gap-2">
          {dummy && <DummyPill />}
          <Button kind="outline" disabled={busy || !selected.length} onClick={runCheck}>Check file</Button>
          <Button disabled={busy || !selected.length} onClick={runCreate}>Create 837 file</Button>
          <Button kind="outline" disabled={busy || !selected.length || !athenaEnabled} onClick={openAthena}
            title={athenaEnabled ? "" : "Turned off on the server (BILLING_ATHENA_SUBMIT)"}>
            Submit via Athena
          </Button>
        </div>
      </div>

      {created && (
        <Card title="File created" right={<Button onClick={() => downloadBatch(created.batch_id, created.file_name)}>Download</Button>}>
          <div className="text-[13px] text-[#374151] space-y-1">
            <div>{created.file_name} · {created.claims} claim(s) · {created.service_lines} line(s) · {money(created.total_charge)} ·
              {" "}{created.total_segments} segments · ISA15 {created.usage_indicator}</div>
            {created.dummy_clearinghouse && <div className="text-amber-700 text-[12px]">Built with DUMMY clearinghouse ids: for format testing only.</div>}
            <Messages warnings={created.validation?.warnings || []} />
          </div>
        </Card>
      )}

      {check && (
        <Card title="File check">
          <div className="space-y-3">
            {check.problems?.length > 0 && <Messages errors={check.problems.map((p) => ({ ...p, field: "" }))} />}
            {env && <div><div className="text-[12px] font-semibold text-[#374151] mb-1">Clearinghouse settings</div>
              <Messages errors={env.errors || []} warnings={env.warnings || []} compact /></div>}
            {(check.warnings || []).length > 0 && <div><div className="text-[12px] font-semibold text-[#374151] mb-1">Claims</div>
              <Messages warnings={check.warnings} compact /></div>}
            {ic && (
              <div>
                <div className="text-[12px] font-semibold text-[#374151] mb-1">837P structure</div>
                <Messages errors={ic.errors || []} warnings={ic.warnings || []} compact />
                {ic.meta && <div className="text-[12px] text-[#6B7280] mt-1">
                  {ic.meta.claims} claim(s), {ic.meta.service_lines} line(s), {ic.meta.total_segments} segments, total {money(ic.meta.total_charge)}, ISA15 {ic.meta.usage_indicator}
                </div>}
              </div>
            )}
          </div>
        </Card>
      )}

      <div className="bg-white rounded-xl border border-[#E5E7EB] overflow-hidden">
        {loading ? (
          <div className="flex justify-center py-12"><CircularProgress size={26} /></div>
        ) : rows.length === 0 ? (
          <div className="text-center text-[13px] text-[#6B7280] py-12">No ready claims in this range. Mark claims ready in the Claims tab.</div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] text-[#6B7280] bg-[#F9FAFB]">
                <th className="px-3 py-2 w-8"><input type="checkbox" aria-label="Select all" checked={allOn}
                  onChange={() => setSelected(allOn ? [] : rows.map((r) => r.appointmentid))} /></th>
                <th className="px-3 py-2">Date</th><th className="px-3 py-2">Patient</th><th className="px-3 py-2">Provider</th>
                <th className="px-3 py-2">Insurance</th><th className="px-3 py-2 text-right">Total</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.appointmentid} className="border-t border-[#F3F4F6]">
                  <td className="px-3 py-2"><input type="checkbox" aria-label={`Select visit ${r.appointmentid}`}
                    checked={selected.includes(r.appointmentid)} onChange={() => toggle(r.appointmentid)} /></td>
                  <td className="px-3 py-2 text-[#374151]">{r.dos}</td>
                  <td className="px-3 py-2 text-[#18181B] font-medium">{r.patient_name}</td>
                  <td className="px-3 py-2 text-[#374151]">{r.providername}</td>
                  <td className="px-3 py-2 text-[12px] text-[#6B7280]">{r.insurance}</td>
                  <td className="px-3 py-2 text-right">{money(r.claim_total)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <Dialog open={Boolean(athena)} onClose={() => !busy && setAthena(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Submit to Athena?</DialogTitle>
        <DialogContent>
          {athena && (
            <div className="space-y-2 text-[13px] text-[#374151]">
              <p>{athena.claims} claim(s) will be created in Athena with the codes, units and diagnoses shown in each claim.
                 Athena then bills them through its own clearinghouse.</p>
              {athena.problems?.length > 0 && <Messages errors={athena.problems.map((p) => ({ ...p, field: "" }))} />}
            </div>
          )}
        </DialogContent>
        <DialogActions>
          <Button kind="ghost" disabled={busy} onClick={() => setAthena(null)}>Cancel</Button>
          <Button disabled={busy || !athena?.confirm_token || athena?.problems?.length > 0} onClick={confirmAthena}>
            Submit {athena?.claims || 0} claim(s)
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default GenerateTab;
